import type { WorkItemInput } from "./workItemsApi.js";
import { getAuthToken } from "../lib/clerkAuth.js";

const DEV_USER_ID = "user_amano_akihiro";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": DEV_USER_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export type WorkItemMaster = {
  id: string;
  name: string;
  description: string | null;
  profession: string;
  default_items_json: string;
  default_items: WorkItemInput[];
  usage_count: number;
  is_pinned: boolean;
  source: "manual" | "ai" | "imported" | "builtin";
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type WorkItemMasterInput = {
  name: string;
  description?: string | null;
  profession: string;
  default_items: WorkItemInput[];
  is_pinned?: boolean;
  source?: "manual" | "ai" | "imported" | "builtin";
};

export const workItemMasterApi = {
  list: (params?: { profession?: string; q?: string }) => {
    const search = new URLSearchParams();
    if (params?.profession) search.set("profession", params.profession);
    if (params?.q) search.set("q", params.q);
    const qs = search.toString();
    return request<{ data: WorkItemMaster[]; total: number }>(`/work-item-master${qs ? `?${qs}` : ""}`);
  },
  create: (body: WorkItemMasterInput) =>
    request<WorkItemMaster>("/work-item-master", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: string, body: Partial<WorkItemMasterInput>) =>
    request<WorkItemMaster>(`/work-item-master/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/work-item-master/${id}`, {
      method: "DELETE",
    }),
  pin: (id: string) =>
    request<{ ok: boolean; is_pinned: boolean }>(`/work-item-master/${id}/pin`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  apply: (id: string, caseId: string) =>
    request<{ data: unknown[]; total: number }>(`/work-item-master/${id}/apply`, {
      method: "POST",
      body: JSON.stringify({ case_id: caseId }),
    }),
};
